import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ArrowRight,
  User,
  Briefcase,
  FileText,
  History,
  ShieldCheck,
  Edit,
  Archive,
  Download,
  Eye,
  Upload,
  CheckCircle,
  X,
  Calendar,
  DollarSign,
  Building2,
  Phone,
  Mail,
  MapPin,
  TrendingUp
} from 'lucide-react';
import AdminLayout from '../../../components/admin/AdminLayout';
import { useAdminAuth } from '../../../contexts/AdminAuthContext';

const EmployeeProfilePage = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { hasPermission } = useAdminAuth();
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('basic');
  const [employee, setEmployee] = useState(null);

  // Load employee data
  useEffect(() => {
    setLoading(true);
    // Mock data
    setTimeout(() => {
      setEmployee({
        id: 'EMP-001',
        fullName: 'أحمد محمد العتيبي',
        position: 'محامي أول',
        department: 'القسم القانوني',
        status: 'active',
        photo: null,
        // Personal Info
        nationalId: '1234567890',
        birthDate: '1990-05-15',
        age: 34,
        nationality: 'سعودي',
        gender: 'male',
        maritalStatus: 'married',
        address: 'الرياض، حي النرجس',
        phone: '+966501234567',
        email: 'ahmed.alotaibi@wazin.law',
        // Job Info
        grade: 'أول',
        directManager: 'سعود إبراهيم المطيري',
        workType: 'full-time',
        attendanceSystem: 'fingerprint',
        workLocation: 'office',
        hireDate: '2020-01-15',
        contractType: 'unlimited',
        workDays: 'الأحد - الخميس',
        workHours: '08:00 - 17:00',
        // Salary
        basicSalary: 15000,
        housingAllowance: 2000,
        transportAllowance: 1000,
        otherAllowances: 500,
        totalSalary: 18500,
        // Bank Info
        bankName: 'البنك الأهلي',
        accountNumber: '1234567890',
        iban: 'SA0380000000608010167519',
        // Emergency Contact
        emergencyName: 'محمد العتيبي',
        emergencyPhone: '+966507654321',
        emergencyRelationship: 'أخ',
        // Status Info
        currentStatus: 'active',
        lastStatusChange: '2020-01-15',
        lastStatusReason: 'تعيين جديد',
        lastStatusBy: 'سعود المطيري',
        // Work History
        workHistory: [
          {
            id: 1,
            type: 'promotion',
            date: '2022-01-01',
            from: 'محامي ثاني',
            to: 'محامي أول',
            reason: 'ترقية بناءً على الأداء المتميز',
            by: 'سعود المطيري',
            notes: 'أداء ممتاز خلال الفترة السابقة'
          },
          {
            id: 2,
            type: 'salary_change',
            date: '2022-01-01',
            from: '12000 ر.س',
            to: '15000 ر.س',
            reason: 'زيادة مع الترقية',
            by: 'سعود المطيري',
            notes: 'زيادة 25%'
          },
          {
            id: 3,
            type: 'transfer',
            date: '2021-06-15',
            from: 'القسم الإداري',
            to: 'القسم القانوني',
            reason: 'نقل حسب الحاجة الوظيفية',
            by: 'إدارة الموارد البشرية',
            notes: 'نقل داخلي'
          }
        ],
        // Contracts
        contracts: [
          {
            id: 1,
            type: 'غير محدد المدة',
            startDate: '2020-01-15',
            endDate: null,
            salary: 18500,
            status: 'active',
            department: 'القسم القانوني',
            position: 'محامي أول',
            trialPeriod: '3 أشهر',
            allowances: 'سكن + نقل + اتصالات'
          },
          {
            id: 2,
            type: 'محدد المدة',
            startDate: '2020-01-15',
            endDate: '2021-01-14',
            salary: 12000,
            status: 'expired',
            department: 'القسم الإداري',
            position: 'محامي ثاني',
            trialPeriod: '3 أشهر',
            allowances: 'نقل'
          }
        ],
        // Documents
        documents: [
          { id: 1, name: 'الهوية الوطنية.pdf', type: 'identity', size: '2.3 MB', uploadDate: '2020-01-10', isPrimary: true },
          { id: 2, name: 'الشهادة الجامعية.pdf', type: 'certificate', size: '1.8 MB', uploadDate: '2020-01-10', isPrimary: false },
          { id: 3, name: 'السيرة الذاتية.pdf', type: 'cv', size: '850 KB', uploadDate: '2020-01-10', isPrimary: false },
          { id: 4, name: 'العقد الوظيفي.pdf', type: 'contract', size: '1.2 MB', uploadDate: '2020-01-15', isPrimary: false }
        ]
      });
      setLoading(false);
    }, 500);
  }, [id]);

  const getStatusBadge = (status) => {
    const statuses = {
      active: { label: 'نشط', color: 'bg-green-100 text-green-700 border-green-300' },
      trial: { label: 'تحت التجربة', color: 'bg-yellow-100 text-yellow-700 border-yellow-300' },
      suspended: { label: 'موقوف', color: 'bg-red-100 text-red-700 border-red-300' },
      resigned: { label: 'مستقيل', color: 'bg-gray-100 text-gray-700 border-gray-300' }
    };
    const statusInfo = statuses[status];
    return (
      <span className={`px-3 py-1 rounded-full text-sm font-bold border-2 ${statusInfo.color}`}>
        {statusInfo.label}
      </span>
    );
  };

  const getHistoryIcon = (type) => {
    switch (type) {
      case 'promotion': return <TrendingUp className="w-5 h-5 text-green-600" />;
      case 'transfer': return <Briefcase className="w-5 h-5 text-blue-600" />;
      case 'salary_change': return <DollarSign className="w-5 h-5 text-yellow-600" />;
      case 'position_change': return <User className="w-5 h-5 text-purple-600" />;
      default: return <History className="w-5 h-5 text-gray-600" />;
    }
  };

  const getHistoryLabel = (type) => {
    switch (type) {
      case 'promotion': return 'ترقية';
      case 'transfer': return 'نقل';
      case 'salary_change': return 'تغيير راتب';
      case 'position_change': return 'تغيير مسمى';
      default: return 'تغيير';
    }
  };

  const getDocumentIcon = (type) => {
    return <FileText className="w-6 h-6 text-gray-400" />;
  };

  const tabs = [
    { id: 'basic', label: 'البيانات الأساسية', icon: User },
    { id: 'history', label: 'السجل الوظيفي', icon: History },
    { id: 'contracts', label: 'العقود', icon: FileText },
    { id: 'documents', label: 'المستندات', icon: FileText },
    { id: 'status', label: 'الحالة الوظيفية', icon: ShieldCheck }
  ];

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-full">
          <div className="text-center">
            <div className="w-16 h-16 border-4 border-[#1E3A8A] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-gray-600">جاري تحميل الملف الوظيفي...</p>
          </div>
        </div>
      </AdminLayout>
    );
  }

  if (!hasPermission('hr.view')) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-full">
          <p className="text-gray-600">ليس لديك صلاحية الوصول لهذا الملف</p>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="p-6 max-w-7xl mx-auto">
        {/* Back Button */}
        <button
          onClick={() => navigate('/admin/hr/employees')}
          className="inline-flex items-center gap-2 text-gray-600 hover:text-[#1E3A8A] mb-4 transition-colors"
        >
          <ArrowRight className="w-5 h-5" />
          <span className="font-semibold">العودة لقائمة الموظفين</span>
        </button>

        {/* Employee Header */}
        <div className="bg-gradient-to-r from-[#1E3A8A] to-[#2d5ab8] rounded-lg shadow-lg p-6 mb-6 text-white">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              {/* Employee Photo Placeholder */}
              <div className="w-20 h-20 bg-white/20 rounded-full flex items-center justify-center">
                <User className="w-10 h-10 text-white" />
              </div>
              
              <div>
                <h1 className="text-3xl font-black mb-2">{employee.fullName}</h1>
                <div className="flex items-center gap-4 text-sm">
                  <span className="flex items-center gap-1">
                    <Briefcase className="w-4 h-4" />
                    {employee.position}
                  </span>
                  <span className="flex items-center gap-1">
                    <Building2 className="w-4 h-4" />
                    {employee.department}
                  </span>
                  <span className="flex items-center gap-1">
                    <User className="w-4 h-4" />
                    {employee.id}
                  </span>
                </div>
              </div>
            </div>

            <div className="flex flex-col items-end gap-3">
              {getStatusBadge(employee.status)}
              
              <div className="flex items-center gap-2">
                {hasPermission('hr.edit') && (
                  <button
                    onClick={() => navigate(`/admin/hr/employees/${id}/edit`)}
                    className="px-4 py-2 bg-white text-[#1E3A8A] rounded-lg font-bold hover:bg-gray-100 transition-all flex items-center gap-2"
                  >
                    <Edit className="w-4 h-4" />
                    تعديل
                  </button>
                )}
                {hasPermission('hr.archive') && (
                  <button
                    onClick={() => alert('أرشفة الموظف')}
                    className="px-4 py-2 bg-red-600 text-white rounded-lg font-bold hover:bg-red-700 transition-all flex items-center gap-2"
                  >
                    <Archive className="w-4 h-4" />
                    أرشفة
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Tabs Navigation */}
        <div className="bg-white rounded-lg shadow-sm border-2 border-gray-200 mb-6">
          <div className="flex border-b-2 border-gray-200 overflow-x-auto">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-2 px-6 py-4 font-bold transition-all whitespace-nowrap ${
                    activeTab === tab.id
                      ? 'border-b-4 border-[#1E3A8A] text-[#1E3A8A] bg-blue-50'
                      : 'text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  <Icon className="w-5 h-5" />
                  {tab.label}
                </button>
              );
            })}
          </div>

          {/* Tab Content */}
          <div className="p-6">
            {/* Tab 1: Basic Information */}
            {activeTab === 'basic' && (
              <div className="space-y-6">
                {/* Personal Information */}
                <div>
                  <h3 className="text-lg font-black text-gray-800 mb-4 flex items-center gap-2">
                    <User className="w-5 h-5 text-[#1E3A8A]" />
                    البيانات الشخصية
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="p-4 bg-gray-50 rounded-lg">
                      <p className="text-xs text-gray-600 mb-1">رقم الهوية</p>
                      <p className="font-bold text-gray-800">{employee.nationalId}</p>
                    </div>
                    <div className="p-4 bg-gray-50 rounded-lg">
                      <p className="text-xs text-gray-600 mb-1">تاريخ الميلاد</p>
                      <p className="font-bold text-gray-800">{employee.birthDate} ({employee.age} سنة)</p>
                    </div>
                    <div className="p-4 bg-gray-50 rounded-lg">
                      <p className="text-xs text-gray-600 mb-1">الجنسية</p>
                      <p className="font-bold text-gray-800">{employee.nationality}</p>
                    </div>
                    <div className="p-4 bg-gray-50 rounded-lg">
                      <p className="text-xs text-gray-600 mb-1">الحالة الاجتماعية</p>
                      <p className="font-bold text-gray-800">{employee.maritalStatus === 'married' ? 'متزوج' : 'أعزب'}</p>
                    </div>
                    <div className="p-4 bg-gray-50 rounded-lg md:col-span-2">
                      <p className="text-xs text-gray-600 mb-1">العنوان</p>
                      <p className="font-bold text-gray-800 flex items-center gap-2">
                        <MapPin className="w-4 h-4 text-gray-600" />
                        {employee.address}
                      </p>
                    </div>
                    <div className="p-4 bg-gray-50 rounded-lg">
                      <p className="text-xs text-gray-600 mb-1">رقم الجوال</p>
                      <p className="font-bold text-gray-800 flex items-center gap-2">
                        <Phone className="w-4 h-4 text-gray-600" />
                        {employee.phone}
                      </p>
                    </div>
                    <div className="p-4 bg-gray-50 rounded-lg">
                      <p className="text-xs text-gray-600 mb-1">البريد الإلكتروني</p>
                      <p className="font-bold text-gray-800 flex items-center gap-2">
                        <Mail className="w-4 h-4 text-gray-600" />
                        {employee.email}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Job Information */}
                <div>
                  <h3 className="text-lg font-black text-gray-800 mb-4 flex items-center gap-2">
                    <Briefcase className="w-5 h-5 text-[#1E3A8A]" />
                    البيانات الوظيفية
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="p-4 bg-gray-50 rounded-lg">
                      <p className="text-xs text-gray-600 mb-1">الدرجة الوظيفية</p>
                      <p className="font-bold text-gray-800">{employee.grade}</p>
                    </div>
                    <div className="p-4 bg-gray-50 rounded-lg">
                      <p className="text-xs text-gray-600 mb-1">المدير المباشر</p>
                      <p className="font-bold text-gray-800">{employee.directManager}</p>
                    </div>
                    <div className="p-4 bg-gray-50 rounded-lg">
                      <p className="text-xs text-gray-600 mb-1">نوع الدوام</p>
                      <p className="font-bold text-gray-800">
                        {employee.workType === 'full-time' ? 'دوام كامل' : employee.workType === 'part-time' ? 'دوام جزئي' : 'عن بُعد'}
                      </p>
                    </div>
                    <div className="p-4 bg-gray-50 rounded-lg">
                      <p className="text-xs text-gray-600 mb-1">نظام الحضور</p>
                      <p className="font-bold text-gray-800">
                        {employee.attendanceSystem === 'fingerprint' ? 'بصمة' : employee.attendanceSystem === 'card' ? 'كرت' : 'يدوي'}
                      </p>
                    </div>
                    <div className="p-4 bg-gray-50 rounded-lg">
                      <p className="text-xs text-gray-600 mb-1">موقع العمل</p>
                      <p className="font-bold text-gray-800">
                        {employee.workLocation === 'office' ? 'المكتب الرئيسي' : employee.workLocation === 'remote' ? 'عن بُعد' : 'مختلط'}
                      </p>
                    </div>
                    <div className="p-4 bg-gray-50 rounded-lg">
                      <p className="text-xs text-gray-600 mb-1">تاريخ التعيين</p>
                      <p className="font-bold text-gray-800 flex items-center gap-2">
                        <Calendar className="w-4 h-4 text-gray-600" />
                        {employee.hireDate}
                      </p>
                    </div>
                    <div className="p-4 bg-gray-50 rounded-lg">
                      <p className="text-xs text-gray-600 mb-1">أيام العمل</p>
                      <p className="font-bold text-gray-800">{employee.workDays}</p>
                    </div>
                    <div className="p-4 bg-gray-50 rounded-lg">
                      <p className="text-xs text-gray-600 mb-1">ساعات الدوام</p>
                      <p className="font-bold text-gray-800">{employee.workHours}</p>
                    </div>
                  </div>
                </div>

                {/* Salary Information */}
                <div>
                  <h3 className="text-lg font-black text-gray-800 mb-4 flex items-center gap-2">
                    <DollarSign className="w-5 h-5 text-[#1E3A8A]" />
                    الراتب والبدلات
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <div className="p-4 bg-gray-50 rounded-lg">
                      <p className="text-xs text-gray-600 mb-1">الراتب الأساسي</p>
                      <p className="font-bold text-gray-800">{employee.basicSalary.toLocaleString()} ر.س</p>
                    </div>
                    <div className="p-4 bg-gray-50 rounded-lg">
                      <p className="text-xs text-gray-600 mb-1">بدل السكن</p>
                      <p className="font-bold text-gray-800">{employee.housingAllowance.toLocaleString()} ر.س</p>
                    </div>
                    <div className="p-4 bg-gray-50 rounded-lg">
                      <p className="text-xs text-gray-600 mb-1">بدل النقل</p>
                      <p className="font-bold text-gray-800">{employee.transportAllowance.toLocaleString()} ر.س</p>
                    </div>
                    <div className="p-4 bg-gray-50 rounded-lg">
                      <p className="text-xs text-gray-600 mb-1">بدلات أخرى</p>
                      <p className="font-bold text-gray-800">{employee.otherAllowances.toLocaleString()} ر.س</p>
                    </div>
                  </div>
                  <div className="mt-4 p-6 bg-gradient-to-r from-[#1E3A8A] to-[#2d5ab8] rounded-lg text-white">
                    <p className="text-sm mb-1">إجمالي الراتب</p>
                    <p className="text-4xl font-black">{employee.totalSalary.toLocaleString()} ر.س</p>
                  </div>
                </div>

                {/* Bank Information */}
                <div>
                  <h3 className="text-lg font-black text-gray-800 mb-4 flex items-center gap-2">
                    <Building2 className="w-5 h-5 text-[#1E3A8A]" />
                    البيانات البنكية
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="p-4 bg-gray-50 rounded-lg">
                      <p className="text-xs text-gray-600 mb-1">اسم البنك</p>
                      <p className="font-bold text-gray-800">{employee.bankName}</p>
                    </div>
                    <div className="p-4 bg-gray-50 rounded-lg">
                      <p className="text-xs text-gray-600 mb-1">رقم الحساب</p>
                      <p className="font-bold text-gray-800">{employee.accountNumber}</p>
                    </div>
                    <div className="p-4 bg-gray-50 rounded-lg md:col-span-2">
                      <p className="text-xs text-gray-600 mb-1">IBAN</p>
                      <p className="font-bold text-gray-800">{employee.iban}</p>
                    </div>
                  </div>
                </div>

                {/* Emergency Contact */}
                <div>
                  <h3 className="text-lg font-black text-gray-800 mb-4 flex items-center gap-2">
                    <Phone className="w-5 h-5 text-[#1E3A8A]" />
                    معلومات الطوارئ
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="p-4 bg-gray-50 rounded-lg">
                      <p className="text-xs text-gray-600 mb-1">الاسم</p>
                      <p className="font-bold text-gray-800">{employee.emergencyName}</p>
                    </div>
                    <div className="p-4 bg-gray-50 rounded-lg">
                      <p className="text-xs text-gray-600 mb-1">رقم الهاتف</p>
                      <p className="font-bold text-gray-800">{employee.emergencyPhone}</p>
                    </div>
                    <div className="p-4 bg-gray-50 rounded-lg">
                      <p className="text-xs text-gray-600 mb-1">العلاقة</p>
                      <p className="font-bold text-gray-800">{employee.emergencyRelationship}</p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Tab 2: Work History */}
            {activeTab === 'history' && (
              <div>
                <h3 className="text-lg font-black text-gray-800 mb-4">السجل الوظيفي</h3>
                {employee.workHistory.length > 0 ? (
                  <div className="space-y-4">
                    {employee.workHistory.map((record) => (
                      <div key={record.id} className="border-2 border-gray-200 rounded-lg p-4 hover:border-[#1E3A8A] transition-colors">
                        <div className="flex items-start gap-4">
                          <div className="p-3 bg-gray-100 rounded-lg">
                            {getHistoryIcon(record.type)}
                          </div>
                          <div className="flex-1">
                            <div className="flex items-center justify-between mb-2">
                              <h4 className="font-bold text-gray-800 text-lg">{getHistoryLabel(record.type)}</h4>
                              <span className="text-sm text-gray-600 flex items-center gap-1">
                                <Calendar className="w-4 h-4" />
                                {record.date}
                              </span>
                            </div>
                            <div className="grid grid-cols-2 gap-4 mb-2">
                              <div>
                                <p className="text-xs text-gray-600">من:</p>
                                <p className="font-bold text-gray-800">{record.from}</p>
                              </div>
                              <div>
                                <p className="text-xs text-gray-600">إلى:</p>
                                <p className="font-bold text-[#1E3A8A]">{record.to}</p>
                              </div>
                            </div>
                            <div className="mb-2">
                              <p className="text-xs text-gray-600">السبب:</p>
                              <p className="text-sm text-gray-800">{record.reason}</p>
                            </div>
                            {record.notes && (
                              <div className="mb-2">
                                <p className="text-xs text-gray-600">ملاحظات:</p>
                                <p className="text-sm text-gray-800">{record.notes}</p>
                              </div>
                            )}
                            <div className="text-xs text-gray-500">
                              بواسطة: {record.by}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    <History className="w-12 h-12 mx-auto mb-2 text-gray-400" />
                    <p>لا توجد سجلات وظيفية</p>
                  </div>
                )}
              </div>
            )}

            {/* Tab 3: Contracts */}
            {activeTab === 'contracts' && (
              <div>
                <h3 className="text-lg font-black text-gray-800 mb-4">العقود</h3>
                {employee.contracts.length > 0 ? (
                  <div className="space-y-4">
                    {employee.contracts.map((contract) => (
                      <div key={contract.id} className="border-2 border-gray-200 rounded-lg p-4">
                        <div className="flex items-start justify-between mb-4">
                          <div>
                            <h4 className="font-bold text-gray-800 text-lg mb-1">{contract.type}</h4>
                            <p className="text-sm text-gray-600">{contract.position} - {contract.department}</p>
                          </div>
                          <span className={`px-3 py-1 rounded-full text-xs font-bold ${
                            contract.status === 'active' 
                              ? 'bg-green-100 text-green-700' 
                              : 'bg-gray-100 text-gray-700'
                          }`}>
                            {contract.status === 'active' ? 'ساري' : 'منتهي'}
                          </span>
                        </div>
                        
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                          <div className="p-3 bg-gray-50 rounded-lg">
                            <p className="text-xs text-gray-600 mb-1">تاريخ البداية</p>
                            <p className="font-bold text-gray-800">{contract.startDate}</p>
                          </div>
                          <div className="p-3 bg-gray-50 rounded-lg">
                            <p className="text-xs text-gray-600 mb-1">تاريخ النهاية</p>
                            <p className="font-bold text-gray-800">{contract.endDate || 'مفتوح'}</p>
                          </div>
                          <div className="p-3 bg-gray-50 rounded-lg">
                            <p className="text-xs text-gray-600 mb-1">الراتب</p>
                            <p className="font-bold text-[#1E3A8A]">{contract.salary.toLocaleString()} ر.س</p>
                          </div>
                          <div className="p-3 bg-gray-50 rounded-lg">
                            <p className="text-xs text-gray-600 mb-1">فترة التجربة</p>
                            <p className="font-bold text-gray-800">{contract.trialPeriod}</p>
                          </div>
                        </div>

                        <div className="mb-4 p-3 bg-blue-50 rounded-lg">
                          <p className="text-xs text-gray-600 mb-1">البدلات</p>
                          <p className="text-sm font-bold text-gray-800">{contract.allowances}</p>
                        </div>

                        {hasPermission('hr.contracts') && (
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => alert(`عرض العقد رقم ${contract.id}`)}
                              className="flex items-center gap-2 px-4 py-2 bg-[#1E3A8A] text-white rounded-lg font-bold hover:bg-[#152d6e] transition-all"
                            >
                              <Eye className="w-4 h-4" />
                              عرض العقد
                            </button>
                            <button
                              onClick={() => alert(`تحميل العقد رقم ${contract.id}`)}
                              className="flex items-center gap-2 px-4 py-2 border-2 border-[#C8A870] text-[#C8A870] rounded-lg font-bold hover:bg-[#C8A870] hover:text-white transition-all"
                            >
                              <Download className="w-4 h-4" />
                              تحميل PDF
                            </button>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    <FileText className="w-12 h-12 mx-auto mb-2 text-gray-400" />
                    <p>لا توجد عقود</p>
                  </div>
                )}
              </div>
            )}

            {/* Tab 4: Documents */}
            {activeTab === 'documents' && (
              <div>
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-black text-gray-800">المستندات</h3>
                  {hasPermission('hr.upload') && (
                    <button
                      onClick={() => alert('رفع مستند جديد')}
                      className="flex items-center gap-2 px-4 py-2 bg-[#C8A870] text-white rounded-lg font-bold hover:bg-[#b8986a] transition-all"
                    >
                      <Upload className="w-4 h-4" />
                      رفع مستند
                    </button>
                  )}
                </div>

                {employee.documents.length > 0 ? (
                  <div className="space-y-3">
                    {employee.documents.map((doc) => (
                      <div
                        key={doc.id}
                        className={`flex items-center justify-between p-4 rounded-lg border-2 ${
                          doc.isPrimary ? 'border-[#C8A870] bg-yellow-50' : 'border-gray-200'
                        }`}
                      >
                        <div className="flex items-center gap-3 flex-1">
                          {getDocumentIcon(doc.type)}
                          <div className="flex-1">
                            <p className="font-bold text-gray-800">{doc.name}</p>
                            <p className="text-xs text-gray-500">{doc.size} • تم الرفع في {doc.uploadDate}</p>
                          </div>
                          {doc.isPrimary && (
                            <span className="px-2 py-1 bg-[#C8A870] text-white text-xs rounded-full font-bold">
                              رئيسي
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => alert(`معاينة: ${doc.name}`)}
                            className="p-2 text-[#1E3A8A] hover:bg-blue-50 rounded-lg transition-colors"
                            title="معاينة"
                          >
                            <Eye className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => alert(`تحميل: ${doc.name}`)}
                            className="p-2 text-[#C8A870] hover:bg-yellow-50 rounded-lg transition-colors"
                            title="تحميل"
                          >
                            <Download className="w-4 h-4" />
                          </button>
                          {hasPermission('hr.upload') && (
                            <button
                              onClick={() => alert(`حذف: ${doc.name}`)}
                              className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                              title="حذف"
                            >
                              <X className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    <FileText className="w-12 h-12 mx-auto mb-2 text-gray-400" />
                    <p>لا توجد مستندات</p>
                  </div>
                )}
              </div>
            )}

            {/* Tab 5: Current Status */}
            {activeTab === 'status' && (
              <div>
                <h3 className="text-lg font-black text-gray-800 mb-4">الحالة الوظيفية الحالية</h3>
                <div className="bg-gradient-to-r from-green-50 to-green-100 border-2 border-green-200 rounded-lg p-6">
                  <div className="flex items-center justify-between mb-6">
                    <div>
                      <p className="text-sm text-gray-600 mb-2">الحالة الحالية</p>
                      {getStatusBadge(employee.currentStatus)}
                    </div>
                    <ShieldCheck className="w-16 h-16 text-green-600 opacity-20" />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="p-4 bg-white rounded-lg">
                      <p className="text-xs text-gray-600 mb-1">تاريخ آخر تغيير</p>
                      <p className="font-bold text-gray-800 flex items-center gap-2">
                        <Calendar className="w-4 h-4 text-gray-600" />
                        {employee.lastStatusChange}
                      </p>
                    </div>
                    <div className="p-4 bg-white rounded-lg">
                      <p className="text-xs text-gray-600 mb-1">من قام بالتعديل</p>
                      <p className="font-bold text-gray-800 flex items-center gap-2">
                        <User className="w-4 h-4 text-gray-600" />
                        {employee.lastStatusBy}
                      </p>
                    </div>
                  </div>

                  <div className="mt-4 p-4 bg-white rounded-lg">
                    <p className="text-xs text-gray-600 mb-1">سبب آخر تغيير</p>
                    <p className="text-sm text-gray-800">{employee.lastStatusReason}</p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default EmployeeProfilePage;
